import { useMutation } from '@apollo/client'
import { AllUsers_users_users, InviteNewUser, InviteNewUserVariables } from '../../types'
import {
  useUserForm,
  userValidationSchemas,
  getUserGqlCompliant,
  UserFieldName,
} from '../../forms/userForm'
import {
  useInsuranceForm,
  getInsuranceGqlCompliant,
  InsuranceField,
} from '../../forms/insuranceForm'
import { INVITE_NEW_USER } from './operations'
export interface FindOrInviteUserProps {
  onUserInvite?: (user: Omit<AllUsers_users_users, '__typename'>) => void
  validationSchema?: keyof typeof userValidationSchemas
  withInsurance?: boolean
}

const useInviteUser = ({
  onUserInvite,
  validationSchema = 'idOrEmailOnly',
}: FindOrInviteUserProps) => {
  const [inviteUser, inviteUserData] = useMutation<InviteNewUser, InviteNewUserVariables>(
    INVITE_NEW_USER
  )
  const insuranceFormNewUser = useInsuranceForm({
    onSubmit: () => {},
    validationSchema: 'fullNewUser',
    initialValues: {
      insuranceCompany: '',
    },
  })

  const userForm = useUserForm({
    onSubmit: async (values, formikHelpers) => {
      try {
        if (insuranceFormNewUser?.values.insuranceCompany) {
          const errors = await insuranceFormNewUser.validateForm()
          if (Object.keys(errors).length) {
            Object.keys(errors).map((key) => {
              insuranceFormNewUser.setFieldTouched(key, true)
            })
            return
          }
        }
        const inviteUserResponse = await inviteUser({
          variables: {
            inputData: {
              ...getUserGqlCompliant(values, [
                UserFieldName.EMAIL,
                UserFieldName.FIRSTNAME,
                UserFieldName.LASTNAME,
                UserFieldName.GENDER,
                UserFieldName.ETHNICITY,
                UserFieldName.RACE,
                UserFieldName.BIRTHDATE,
                UserFieldName.PHONE_NUMBER,
                UserFieldName.ADDRESS,
                UserFieldName.CDC_CARD,
                UserFieldName.PHOTO_ID,
                UserFieldName.USER_ROLES,
                UserFieldName.PASSWORD,
              ]),
              insurance: getInsuranceGqlCompliant(insuranceFormNewUser.values, [
                InsuranceField.SSN,
                InsuranceField.CARDID,
                InsuranceField.DRIVERLICENSE,
                InsuranceField.GOVID,
                InsuranceField.GROUPNUMBER,
                InsuranceField.INSURANCECARDBACK,
                InsuranceField.INSURANCECARDFRONT,
                InsuranceField.INSURANCECOMPANY,
                InsuranceField.PLAN,
                InsuranceField.RELATIONTOSUBSCRIBER,
                InsuranceField.SUBSCRIBER_NAME,
                InsuranceField.SUBSCRIBER_MIDDLE_NAME,
                InsuranceField.SUBSCRIBER_LAST_NAME,
                InsuranceField.SUBSCRIBER_DOB,
                InsuranceField.SUBSCRIBER_GENDER,
              ]),
            },
          },
        })

        const user = inviteUserResponse.data?.inviteUser
        if (user) {
          onUserInvite?.((user as unknown) as Omit<AllUsers_users_users, '__typename'>)
          formikHelpers.resetForm()
          insuranceFormNewUser.resetForm()
        }
      } catch {
        // ignore error
      }
    },
    validationSchema,
  })

  return {
    userInviteForm: userForm,
    inviteUserData,
    insuranceFormNewUser,
  }
}

export { useInviteUser }
